import { LogLevel, type Level, type LoggerConfig, type LogParams } from './types';

class Logger {
  constructor(
    private readonly config: LoggerConfig = {
      level: LogLevel.INFO,
      enableConsole: true,
      ...config,
    },
    private readonly context = 'Global',
  ) {}

  private log<TLogLevel extends Level>(
    level: TLogLevel,
    message: LogParams<TLogLevel>[0],
    ...data: LogParams<TLogLevel>[1][]
  ) {
    if (!this.shouldLog(level)) return;

    const payload = {
      message,
      level,
      timestamp: new Date().toISOString(),
      context: this.context,
      data,
    };

    if (this.config.enableConsole) {
      const prefix = `[${payload.context}]`;
      console[level](prefix, payload.message, payload);
    }
  }

  private shouldLog(level: Level) {
    const levels: Level[] = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    return levels.indexOf(level) >= levels.indexOf(this.config.level ?? LogLevel.INFO);
  }

  error(message: LogParams<LogLevel.ERROR>[0], ...data: LogParams<LogLevel.ERROR>[1]) {
    this.log(LogLevel.ERROR, message, ...data);
  }

  warn(message: LogParams<LogLevel.WARN>[0], ...data: LogParams<LogLevel.WARN>[1]) {
    this.log(LogLevel.WARN, message, ...data);
  }

  info(message: LogParams<LogLevel.INFO>[0], ...data: LogParams<LogLevel.INFO>[1]) {
    this.log(LogLevel.INFO, message, ...data);
  }

  debug(message: LogParams<LogLevel.DEBUG>[0], ...data: LogParams<LogLevel.DEBUG>[1]) {
    this.log(LogLevel.DEBUG, message, ...data);
  }

  withContext(context: string) {
    return new Logger(this.config, context);
  }
}

export { Logger };
